import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  CATEGORY_LABEL,
  formatDateTime,
  formatYen,
  type DashboardSummary,
} from '../components/api'
import { Alert, Badge, Card, EmptyState } from '../components/ui'

export function DashboardPage() {
  const [data, setData] = useState<DashboardSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<DashboardSummary>('/api/dashboard/summary')
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const upcomingTotal = data
    ? data.expiring_30d.domains +
      data.expiring_30d.ssl +
      data.expiring_30d.servers +
      data.expiring_30d.contracts
    : 0

  return (
    <>
      <div className="page-header">
        <h1>ダッシュボード</h1>
        <Link to="/alerts" className="btn btn-secondary btn-sm">
          期限間近を見る
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Card title="期限間近（30日以内）">
          {loading ? (
            <p className="muted">読み込み中…</p>
          ) : (
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {upcomingTotal}
                <span className="subtle" style={{ fontSize: 14, marginLeft: 6 }}>
                  件
                </span>
              </div>
              {data && (
                <ul
                  className="stack-2"
                  style={{
                    margin: '12px 0 0',
                    padding: 0,
                    listStyle: 'none',
                    fontSize: 12,
                  }}
                >
                  <li className="row" style={{ justifyContent: 'space-between' }}>
                    <span>ドメイン</span>
                    <Badge variant="muted">{data.expiring_30d.domains}</Badge>
                  </li>
                  <li className="row" style={{ justifyContent: 'space-between' }}>
                    <span>SSL</span>
                    <Badge variant="muted">{data.expiring_30d.ssl}</Badge>
                  </li>
                  <li className="row" style={{ justifyContent: 'space-between' }}>
                    <span>サーバー</span>
                    <Badge variant="muted">{data.expiring_30d.servers}</Badge>
                  </li>
                  <li className="row" style={{ justifyContent: 'space-between' }}>
                    <span>保守契約</span>
                    <Badge variant="muted">{data.expiring_30d.contracts}</Badge>
                  </li>
                </ul>
              )}
            </div>
          )}
        </Card>

        <Card title="継続収益（月額合計）">
          {loading ? (
            <p className="muted">読み込み中…</p>
          ) : (
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {formatYen(data?.recurring_monthly_total)}
              </div>
              <p className="subtle" style={{ margin: '8px 0 0', fontSize: 12 }}>
                稼働中の保守・運用契約の月額合計（税抜）
              </p>
            </div>
          )}
        </Card>

        <Card title="顧客マスタ">
          <Link to="/customers" className="btn btn-secondary btn-block">
            顧客一覧を開く
          </Link>
          <div style={{ marginTop: 8 }}>
            <Link to="/customers/new" className="btn btn-ghost btn-block btn-sm">
              ＋ 新規顧客を登録
            </Link>
          </div>
        </Card>
      </div>

      <Card
        title="直近の対応履歴"
        actions={
          <Link to="/reports" className="btn btn-ghost btn-sm">
            すべて見る
          </Link>
        }
      >
        {loading ? (
          <p className="muted">読み込み中…</p>
        ) : !data || data.recent_reports.length === 0 ? (
          <EmptyState
            title="対応履歴がありません"
            description="顧客対応を記録すると、ここに最新分が表示されます。"
            action={
              <Link to="/reports/new" className="btn btn-primary btn-sm">
                対応履歴を登録
              </Link>
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 140 }}>発生日時</th>
                <th style={{ width: 110 }}>カテゴリ</th>
                <th>顧客</th>
                <th>内容</th>
                <th style={{ width: 100 }}>起票者</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_reports.map((r) => (
                <tr key={r.id}>
                  <td>{formatDateTime(r.occurred_at)}</td>
                  <td>
                    <Badge variant="muted">{CATEGORY_LABEL[r.category]}</Badge>
                  </td>
                  <td>
                    {r.customer_id ? (
                      <Link to={`/customers/${r.customer_id}`}>
                        {r.customer_name ?? '—'}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{truncate(r.body, 60)}</td>
                  <td className="muted">{r.user_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

function truncate(s: string, n: number) {
  if (!s) return ''
  return s.length > n ? `${s.slice(0, n)}…` : s
}
